import React, { useState, useEffect } from "react";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { PokemonSprite } from "../pokemon/pokemon-sprite";
import { Direction } from "types/battle-engine/battle";
import { PokemonSkillCard } from "../pokemon/pokemon-skill-card";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";

interface StarterRevealProps {
  pokemonName: string;
  activeSkills?: string[] | undefined;
  onComplete: () => void;
  confirmText?: string;
}

const directions: Direction[] = [
  Direction.S,
  Direction.SE,
  Direction.E,
  Direction.NE,
  Direction.N,
  Direction.NW,
  Direction.W,
  Direction.SW,
];

export const StarterReveal: React.FC<StarterRevealProps> = ({
  pokemonName,
  activeSkills = [],
  onComplete,
  confirmText = "Begin Journey",
}) => {
  const [phase, setPhase] = useState<"charging" | "revealing" | "done">(
    "charging",
  );

  const [direction, setDirection] = useState<Direction>(Direction.S);

  useEffect(() => {
    const interval = setInterval(() => {
      const newDirection =
        directions[Math.floor(Math.random() * directions.length)];
      if (newDirection) {
        setDirection(newDirection);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Phase 1: Charging (Wait for a bit before the big flash)
    const timer1 = setTimeout(() => {
      setPhase("revealing");
    }, 1500);

    // Phase 2: Show the pokemon (Revealing)
    const timer2 = setTimeout(() => {
      setPhase("done");
    }, 4500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="relative flex flex-col justify-center items-center w-full h-full min-h-[400px]">
      {/* Background Glow */}
      <div
        className={`absolute w-64 h-64 rounded-full blur-3xl transition-all duration-1000 ${
          phase === "charging"
            ? "bg-white/5 scale-50"
            : "bg-pk-blue-3/40 scale-150"
        }`}
      />

      {/* VFX Effects */}
      {phase === "revealing" && (
        <>
          <AnimatedVFX
            id="reveal-sparkles"
            spriteUrl="/assets/spells/vfx/self_sparkles_000__48.png"
            position={{ x: 0, y: 0 }}
            spriteScale={4}
            className="top-1/2 left-1/2 absolute -translate-x-1/2 -translate-y-1/2"
            onComplete={() => console.log("Sparkles done")}
          />
          <AnimatedVFX
            id="reveal-stars"
            spriteUrl="/assets/spells/vfx/stars_effect_000__40.png"
            position={{ x: 0, y: 0 }}
            spriteScale={3}
            className="top-1/2 left-1/2 absolute -translate-x-1/2 -translate-y-1/2"
          />
        </>
      )}

      {/* Pokemon Display */}
      <div
        className={`relative z-10 flex flex-col items-center transition-all duration-1000 ${
          phase === "done" ? "opacity-100 scale-100" : "opacity-0 scale-50"
        }`}
      >
        <div className="flex justify-center items-center mb-6 w-48 h-48">
          <PokemonSprite
            pokemon={{ base_pokemon_name: pokemonName }}
            style={{ scale: 6 }}
            showShadow={true}
            animation="Idle"
            direction={direction}
          />
        </div>

        <div className="justify-center gap-3 grid grid-cols-3 mb-8 max-w-sm">
          {activeSkills.map((skillId) => {
            const skill = POKEMON_SKILLS[skillId];
            if (!skill) return null;
            return <PokemonSkillCard key={skillId} compact skill={skill} />;
          })}
        </div>

        <h2 className="drop-shadow-lg font-black text-white text-4xl italic uppercase tracking-tighter">
          {pokemonName}
        </h2>
        <p className="mt-2 font-black text-pk-blue-3 text-sm uppercase tracking-[0.3em]">
          Your first partner
        </p>

        <button
          onClick={onComplete}
          className="bg-white shadow-xl mt-12 px-12 py-4 rounded-xl font-black text-pk-night-2 text-lg uppercase tracking-widest hover:scale-105 active:scale-95 transition-all"
        >
          {confirmText}
        </button>
      </div>

      {/* Initial Secret Message */}
      {phase === "charging" && (
        <div className="font-black text-white/20 text-2xl italic uppercase tracking-[0.5em] animate-pulse">
          Summoning...
        </div>
      )}
    </div>
  );
};
